import axios from 'axios';
import { type Location, type NavigateFunction } from 'react-router-dom';
import { getCurrentUserIdToken } from 'src/modules/firebase';
import { getBackendUrl } from 'src/config/backend';
import { ROUTES } from 'src/config/routes';

export const publicPages: string[] = [
  ROUTES.ROOT.path,
  ROUTES.LOGIN.path,
  ROUTES.RESET_PASSWORD.path,
  ROUTES.AUTH_ACTION.path,
  ROUTES.INVITATIONS.path,
  ROUTES.TERMS_AND_CONDITIONS.path,
  ROUTES.CONFIDENTIALITE.path,
];
type UnauthorizedCandidate = { response?: { status?: number } } | null | undefined;

export const useOnUnauthorizedError =
  (
    navigate: NavigateFunction,
    location: Location,
    setIsLoggedIn: (value: boolean) => void,
  ) =>
  (error: UnauthorizedCandidate): Promise<never> | undefined => {
    if (error) {
      if (axios.isCancel(error)) return;
      if (error.response?.status === 401) {
        setIsLoggedIn(false);
        if (!publicPages.includes(location.pathname))
          navigate({
            pathname: ROUTES.ROOT.path,
          });
      }
    }

    return Promise.reject(error);
  };

export const initAxios = () => {
  // Captured once at init.
  axios.defaults.baseURL = String(getBackendUrl());
  // Axios's default is 0 (wait forever): a request stuck on a dead socket then hangs until the OS
  // drops the connection (~30 min seen on staging), and react-query dedupes remounts into the hung
  // fetch, wedging the screen. 120s is ~80× the slowest real API call; on expiry the request fails
  // as a normal network error (no response) and the existing retry/backoff takes over. Heavy
  // uploads (Firebase Storage SDK) are not governed by this default.
  axios.defaults.timeout = 120_000;
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common.Accept = 'application/json';
  axios.defaults.paramsSerializer = {
    indexes: null,
  };

  axios.interceptors.request.use(async function (config) {
    const url = config.url;
    if (
      !url ||
      (!url.startsWith(getBackendUrl()) &&
        !url.startsWith('/api'))
    ) {
      return config;
    }

    const token = await getCurrentUserIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });
};
